import {ref, Ref, watch} from "vue";
import PrescriptionPages from "@/medication-checkup/services/PrescriptionPages";

/**
 * A page of a scanned prescription, shown once it is asked for and never
 * ahead. Nothing is fetched while `wanted` is false, so a sheet out of view
 * costs nothing; the pages themselves come from the shared cache.
 */
export const usePrescriptionPage = (
    key: Ref<string | null>,
    page: Ref<number>,
    wanted: Ref<boolean> = ref(true)
) => {
    const src = ref<string | null>(null);
    const pageCount = ref(1);
    const loading = ref(false);
    const failed = ref(false);

    const show = () => {
        if (key.value === null || !wanted.value) {
            return;
        }

        const requested = {key: key.value, page: page.value};
        const current = () => requested.key === key.value && requested.page === page.value;

        loading.value = true;
        failed.value = false;

        PrescriptionPages.fetch(requested.key, requested.page)
            .then(shown => {
                if (requested.page === 1 && requested.key === key.value) {
                    pageCount.value = shown.pageCount;
                }

                if (current()) {
                    src.value = shown.url;
                }
            })
            .catch(() => {
                // The page left on screen while this one loaded is not the
                // one asked for: it goes, so the failure shows instead.
                if (current()) {
                    src.value = null;
                    failed.value = true;
                }
            })
            .finally(() => {
                if (current()) {
                    loading.value = false;
                }
            });
    };

    watch(key, () => {
        src.value = null;
        pageCount.value = 1;
    });

    watch([key, page, wanted], show, {immediate: true});

    /**
     * Ask for the page again after a failure — the cache forgot it, so a
     * passing refusal of the HDS does not stick for the rest of the review.
     */
    const retry = () => show();

    return {src, pageCount, loading, failed, retry};
};
